import { createFileRoute, Link } from "@tanstack/react-router";
import { abs } from "@/lib/site";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { decorativeCollections } from "@/data/decorative";

export const Route = createFileRoute("/decorative/")({
  head: () => {
    const title = "Decorative Text Symbols — borders, dividers, loading bars & aesthetic combos";
    const description =
      "Ready-made decorative text combos to copy and paste: box borders, line dividers, loading bars and tiny aesthetic flourishes for bios, posts and profiles.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: "Decorative Text Symbols" },
        { property: "og:description", content: description },
        { property: "og:url", content: abs("/decorative") },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: abs("/decorative") }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Decorative Text Symbols",
            description,
            url: "/decorative",
            numberOfItems: decorativeCollections.length,
          }),
        },
      ],
    };
  },
  component: DecorativeIndex,
});

function DecorativeIndex() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <section className="mx-auto w-full max-w-7xl px-4 pb-6 pt-8 sm:px-6 sm:pt-16">
          <nav className="mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground sm:text-sm">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <span>/</span>
            <span className="text-foreground">Decorative</span>
          </nav>
          <h1 className="font-display text-3xl font-bold sm:text-5xl">Decorative Text Symbols</h1>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground sm:text-base">
            Ready-made multi-character combos you copy as a whole — box borders, line dividers, loading bars and
            tiny aesthetic flourishes for bios, posts and profiles.
          </p>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 pb-16 sm:px-6">
          <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {decorativeCollections.map((c) => (
              <li key={c.slug}>
                <Link
                  to="/decorative/$slug"
                  params={{ slug: c.slug }}
                  className="glass flex h-full flex-col rounded-xl p-5 transition-colors hover:border-primary/40"
                >
                  <span className="mb-3 flex min-h-[3.5rem] min-w-0 items-center justify-center overflow-x-auto rounded-lg border border-border bg-surface/60 px-3 py-2">
                    <span className="whitespace-pre text-center font-combo text-lg text-foreground/90">{c.hero}</span>
                  </span>
                  <span className="font-display text-lg font-semibold">{c.name}</span>
                  <span className="mt-1 text-sm text-muted-foreground">{c.tagline}</span>
                  <span className="mt-3 text-xs text-muted-foreground">{c.combos.length} combos →</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
